import { SplittingGame } from '../../games/splitting/SplittingGame.js';
import { SplittingVortex } from '../../games/splitting/SplittingVortex.js';
import { SplittingRenderer } from '../../games/splitting/SplittingRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Splitting-specific types ──────────────────────────────────────

export interface SplittingRenderOptions {
    // Placeholder — SplittingRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class SplittingTutorialAdapter implements TutorialGameAdapter {
    private game: SplittingGame;
    private renderer: SplittingRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new SplittingGame(grid);
        this.renderer = new SplittingRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();

        if (!raw) return;

        this.game.deserialize(raw);
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createSplittingTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new SplittingTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface SplittingVortexTutorialState {
    splitting: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class SplittingVortexTutorialAdapter implements TutorialGameAdapter {
    private game: SplittingVortex;
    private renderer: SplittingRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new SplittingVortex(grid);
        this.renderer = new SplittingRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as SplittingVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (!s) return;

        if (s.splitting) {
            this.game.deserialize(s.splitting);
        }

        this.currentGateMode = s.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createSplittingVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 3 }, { row: 2, col: 4 }],  // D4 ↔ E2
    ];
    return new SplittingVortexTutorialAdapter(canvas, grid, gatePairs);
}
